import { motion } from 'framer-motion';
import { Eye, Leaf } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { If } from '@components/condition';
import { IconCheck } from '@components/shared';
import { AuthModal } from '@features/auth/AuthModal';
import { modalActions } from '@store/actions';
import { userSelector } from '@store/user/user.slice';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 100 },
  },
};

const featureVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.8 + i * 0.1, type: 'spring', stiffness: 100 },
  }),
};

const decorVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut' },
  },
};

function SectionBanner() {
  const dispatch = useDispatch();
  const { isAuth } = useSelector(userSelector);

  const handleOpenAuthModal = useCallback(() => {
    dispatch(
      modalActions.openModal({
        modalId: 'auth-modal',
        modalComponent: <AuthModal modalId="auth-modal" />,
      })
    );
  }, [dispatch]);

  const profileLink = useMemo(() => {
    return `/preview?title=${encodeURI('Hồ sơ năng lực')}&file=${encodeURI(
      'https://thucphamquangda.s3.ap-southeast-1.amazonaws.com/images/Ho%CC%82%CC%80+so%CC%9B+na%CC%86ng+lu%CC%9B%CC%A3c+CTY+QDF.pdf'
    )}`;
  }, []);

  return (
    <section className="relative overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={decorVariants}
        className="absolute -left-16 -top-16 h-64 w-64 rounded-full bg-green-100/50"
      />
      <motion.div
        initial="hidden"
        animate="visible"
        variants={decorVariants}
        className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-orange-100/30"
      />
      <div className="container-content relative mx-auto py-6">
        <div className="flex flex-col items-center gap-6 md:flex-row md:gap-10">
          {/* Left content */}
          <motion.div
            className="flex-1 space-y-6 text-center md:text-left"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <motion.div
                className="inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-1.5 text-sm font-medium text-green-800"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Leaf size={16} />
                <span>100% Tươi Ngon</span>
              </motion.div>
            </motion.div>
            <motion.h1
              variants={itemVariants}
              className="text-2xl tracking-tight md:text-4xl xl:text-5xl flex items-center justify-center md:justify-start gap-2"
            >
              <motion.span
                className="block text-green-900 font-bold"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                THỰC PHẨM
              </motion.span>
              <motion.span
                className="block text-orange-600 font-bold"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                QUẢNG ĐÀ
              </motion.span>
            </motion.h1>
            <motion.p variants={itemVariants} className="text-sm text-gray-600 md:text-lg">
              Cung cấp nguyên liệu và sản phẩm cho bếp ăn công nghiệp, bếp trường học, resort, nhà
              hàng khách sạn. Sơ chế nguyên liệu theo yêu cầu khách hàng.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-wrap justify-center gap-2 md:gap-4 md:justify-start mt-4 md:mt-6"
            >
              <Link href="/san-pham" legacyBehavior>
                <a className="cursor-pointer bg-orange-600 text-white font-bold px-6 py-2 md:py-3 rounded-lg shadow transition-all text-base md:text-lg w-auto text-center flex items-center">
                  Đặt hàng ngay
                </a>
              </Link>

              <If
                condition={!isAuth}
                Then={
                  <button
                    onClick={handleOpenAuthModal}
                    className="cursor-pointer border-2 border-orange-600 text-orange-600 font-bold px-6 py-2 md:py-3 rounded-lg shadow transition-all text-base md:text-lg w-auto text-center flex items-center"
                  >
                    Đăng ký ngay
                  </button>
                }
              />
            </motion.div>
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap items-center justify-center gap-8 pt-4 md:justify-start"
            >
              <motion.div
                className="flex items-center gap-2"
                variants={featureVariants}
                custom={0}
                initial="hidden"
                animate="visible"
              >
                <div className="rounded-full bg-green-100 p-2 text-green-600">
                  <IconCheck />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-start">Chứng nhận</span>
                  <span className="text-sm font-medium">An toàn thực phẩm</span>
                </div>
              </motion.div>
              <motion.div
                className="flex items-center gap-2"
                variants={featureVariants}
                custom={1}
                initial="hidden"
                animate="visible"
              >
                <div className="rounded-full bg-green-100 p-2 text-green-600">
                  <IconCheck />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-start">Tiêu chuẩn</span>
                  <span className="text-sm font-medium">HCCP, ISO 22000</span>
                </div>
              </motion.div>
              <motion.div
                className="flex items-center gap-2"
                variants={featureVariants}
                custom={2}
                initial="hidden"
                animate="visible"
              >
                <div className="rounded-full bg-green-100 p-2 text-green-600">
                  <IconCheck />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-start">Chứng nhận</span>
                  <span className="text-sm font-medium">Hiệu chuẩn</span>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right image */}
          <motion.div
            className="relative flex-1 w-full flex justify-center"
            initial="hidden"
            animate="visible"
            variants={featureVariants}
            custom={3}
          >
            <div className="w-[350px] md:h-[500px] sm:h-[570px] overflow-hidden rounded-md flex justify-center relative group shadow-lg cursor-pointer">
              <Image
                src="/img/banner_profile.png"
                alt="Hồ sơ năng lực qdfood"
                width={400}
                height={550}
                priority
                className="rounded-md object-cover"
              />

              {/* Overlay khi hover */}
              <Link
                href={profileLink}
                target="_blank"
                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
              >
                <div className="flex flex-col items-center gap-2">
                  <button
                    aria-label="Xem hồ sơ năng lực"
                    className="w-12 h-12 p-3 bg-white rounded-full shadow-lg hover:scale-110 transition-transform"
                  >
                    <Eye />
                  </button>
                  <p className="text-white">Xem hồ sơ năng lực</p>
                </div>
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="mt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8 text-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={featureVariants}
              custom={4}
              className="bg-white p-3 md:p-6 rounded-lg shadow-lg"
            >
              <div className="text-3xl font-bold text-purple-600 mb-2 flex items-center justify-center gap-1">
                500m²
              </div>
              <div className="text-gray-600">Quy mô nhà xưởng</div>
            </motion.div>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={featureVariants}
              custom={5}
              className="bg-white p-3 md:p-6 rounded-lg shadow-lg"
            >
              <div className="text-3xl font-bold text-green-600 mb-2">20+</div>
              <div className="text-gray-600">Lao động chuyên nghiệp</div>
            </motion.div>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={featureVariants}
              custom={6}
              className="bg-white p-3 md:p-6 rounded-lg shadow-lg"
            >
              <div className="text-3xl font-bold text-yellow-800 mb-2">1,000+</div>
              <div className="text-gray-600">Tấn/năm sản lượng</div>
            </motion.div>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={featureVariants}
              custom={7}
              className="bg-white p-3 md:p-6 rounded-lg shadow-lg"
            >
              <div className="text-3xl font-bold text-orange-600 mb-2">2+</div>
              <div className="text-gray-600">Năm kinh nghiệm</div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default memo(SectionBanner);
